import React, { useState } from 'react';
import { useLang } from '../i18n/translations';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';

export default function Kontakt() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: '', email: '', title: '', message: '' });
  const [notRobot, setNotRobot] = useState(false);
  const [sending, setSending] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.title || !form.message) {
      toast.error('Molimo popunite sva polja.');
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) {
      toast.error('Unesite ispravnu e-mail adresu.');
      return;
    }
    if (!notRobot) {
      toast.error('Molimo potvrdite da niste robot.');
      return;
    }
    setSending(true);
    api.post('/inquiries', { ...form, type: 'kontakt' })
      .then(() => {
        toast.success('Poruka je uspješno poslana! Službeno lice će vas kontaktirati u najkraćem roku.');
        setForm({ name: '', email: '', title: '', message: '' });
        setNotRobot(false);
      })
      .catch(() => toast.error('Greška pri slanju poruke. Pokušajte ponovo.'))
      .finally(() => setSending(false));
  };

  return (
    <div className="bg-[#f8f9fd] min-h-screen">
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-4xl font-bold text-[#0b1a3d] mb-2" data-testid="kontakt-title">{t('contact')}</h1>
          <p className="text-slate-500">Kontaktirajte Ministarstvo privrede Srednjobosanskog kantona / Kantona Središnja Bosna.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid lg:grid-cols-3 gap-8">
        <div className="space-y-4">
          {[
            { icon: MapPin, title: 'Adresa', lines: ['ul. Prnjavor 16', '72270 Travnik', 'Bosna i Hercegovina'] },
            { icon: Phone, title: 'Telefon', lines: ['+387 (0)30 511 217'] },
            { icon: Mail, title: 'E-mail', lines: ['info@eprivreda.ba'] },
            { icon: Clock, title: 'Radno vrijeme', lines: ['Ponedjeljak – Petak', '08:00 – 16:00'] }
          ].map((c, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-100 p-5 flex gap-4">
              <div className="w-11 h-11 rounded-lg bg-[#e8ecf7] flex items-center justify-center shrink-0">
                <c.icon className="h-5 w-5 text-[#001752]" />
              </div>
              <div>
                <p className="font-semibold text-[#0b1a3d]">{c.title}</p>
                {c.lines.map((l, j) => <p key={j} className="text-sm text-slate-500">{l}</p>)}
              </div>
            </div>
          ))}
        </div>

        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-100 p-8" data-testid="kontakt-form">
          <h2 className="text-xl font-bold text-[#0b1a3d] mb-6">{t('contactUs')}</h2>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-600 mb-1.5 block">{t('name')} *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" className="border-slate-200" />
              </div>
              <div>
                <Label className="text-slate-600 mb-1.5 block">{t('email')} *</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" className="border-slate-200" />
              </div>
            </div>
            <div>
              <Label className="text-slate-600 mb-1.5 block">{t('title')} *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="contact-subject" className="border-slate-200" />
            </div>
            <div>
              <Label className="text-slate-600 mb-1.5 block">{t('message')} *</Label>
              <Textarea rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" className="border-slate-200" />
            </div>
            <div className="flex items-center gap-2.5 bg-[#f8f9fd] border border-slate-200 rounded-lg p-4 w-fit">
              <Checkbox id="robot" checked={notRobot} onCheckedChange={(v) => setNotRobot(!!v)} data-testid="contact-robot-check" />
              <Label htmlFor="robot" className="text-sm text-slate-600 cursor-pointer">Nisam robot</Label>
            </div>
            <Button type="submit" disabled={sending} data-testid="contact-submit"
              className="bg-[#001752] hover:bg-[#0a2a7a] text-white rounded-md px-8 h-11 transition-colors duration-200">
              <Send className="h-4 w-4 mr-2" /> {sending ? '...' : t('send')}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
